/**
 * @file RbtInterviewQuestions.jsx
 * @description Standalone SEO/career-content page at /rbt-interview-questions —
 *   practical interview prep for RBTs, BTs, and ABA therapists evaluating ABA
 *   case opportunities: what agencies tend to ask, what candidates should ask
 *   back, and how to prepare honestly. Interview-logistics and fit framing
 *   only, not clinical guidance. Mirrors the structure of AboutAbaCases.jsx.
 * @module components/RbtInterviewQuestions
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  MapPinIcon, DollarIcon, CalendarIcon, ShieldIcon, BriefcaseIcon,
  MessageIcon, BellIcon, ClipboardIcon, CheckCircleIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import GuideReviewStamp from "./GuideReviewStamp";
import { GuideYourPath, GuideYourPathChips } from "./GuideShared";
import { setSignupIntent } from "../lib/signupIntent";
import { useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A, ctaMeta } from "../lib/analytics";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

export default function RbtInterviewQuestions({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  usePageSeo({
    title: "RBT Interview Questions for ABA Case Opportunities | ABAcases",
    description: "Prepare for RBT and behavior technician interviews with practical questions about ABA settings, schedules, supervision, data collection, and case fit.",
    canonical: "/rbt-interview-questions",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-rbt-interview-questions", [
    { name: "Home", url: "/" },
    { name: "RBT Interview Questions", url: "/rbt-interview-questions" },
  ]);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as AboutAbaCases / CityRolePage.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  const goJoin = () => {
    track(A.RESOURCE_PAGE_CTA_CLICK, {
      supabaseMeta: { page: "/rbt-interview-questions/" },
      ga4Params: ctaMeta({
        pagePath: "/rbt-interview-questions/",
        pageType: "resource",
        ctaLabel: "Join ABA case alerts",
        targetPath: "/auth",
        userLanguage: i18n.language,
        audience: "resource",
      }),
    });
    setSignupIntent("alerts");
    onNavigate("auth");
  };

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 900;

  // Reusable question/answer style card grid used across several sections.
  const QGrid = ({ items }) => (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16 }}>
      {items.map((c) => (
        <div key={c.key} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
          <div style={{ width: 42, height: 42, borderRadius: 11, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <c.Icon size={19} color={INDIGO} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 5 }}>{c.title}</div>
            <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }}>{c.body}</div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs items={[
            { label: t('rbtInterviewQuestions.breadcrumbHome'), onClick: () => onNavigate("home") },
            { label: t('rbtInterviewQuestions.breadcrumbCurrent') },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t('rbtInterviewQuestions.badgeInterviewPrep')}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>abacases.com</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t('rbtInterviewQuestions.heroTitle')}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 16px" }}>
            {t('rbtInterviewQuestions.heroSubtitle')}
          </p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 640, margin: "0 0 28px" }}>
            {t('rbtInterviewQuestions.heroIntroPre')}{" "}
            <SeoLink to="rbt-resume-guide" style={{ color: "#fff", fontWeight: 700, borderBottom: "1px solid rgba(255,255,255,0.4)" }}>{t('rbtInterviewQuestions.heroResumeGuideLink')}</SeoLink>{" "}
            {t('rbtInterviewQuestions.heroIntroPost')}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={goJoin} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: `linear-gradient(135deg, ${INDIGO} 0%, #6366F1 100%)`, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 24px rgba(79,70,229,0.4)" }}>
              <BellIcon size={16} color="#fff" /> {t('rbtInterviewQuestions.joinFreeButton')}
            </button>
          </div>
        </div>
      </header>
      <GuideYourPathChips current="rbt-interview-questions" onNavigate={onNavigate} isMobile={isMobile} />

      {/* ── 1. Questions agencies may ask RBTs and BTs ────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('rbtInterviewQuestions.section1Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 20 }}>
            {t('rbtInterviewQuestions.section1Intro')}
          </p>
          <QGrid items={[
            { key: "s1q1", Icon: BriefcaseIcon, title: t('rbtInterviewQuestions.s1q1Title'), body: t('rbtInterviewQuestions.s1q1Body') },
            { key: "s1q2", Icon: ClipboardIcon, title: t('rbtInterviewQuestions.s1q2Title'), body: t('rbtInterviewQuestions.s1q2Body') },
            { key: "s1q3", Icon: MessageIcon, title: t('rbtInterviewQuestions.s1q3Title'), body: t('rbtInterviewQuestions.s1q3Body') },
            { key: "s1q4", Icon: CalendarIcon, title: t('rbtInterviewQuestions.s1q4Title'), body: t('rbtInterviewQuestions.s1q4Body') },
          ]} />
        </div>
      </section>

      {/* ── 2. Questions RBTs, BTs, and ABA therapists should ask agencies ── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('rbtInterviewQuestions.section2Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 20 }}>
            {t('rbtInterviewQuestions.section2Intro')}
          </p>
          <QGrid items={[
            { key: "s2q6", Icon: DollarIcon, title: t('rbtInterviewQuestions.s2q6Title'), body: t('rbtInterviewQuestions.s2q6Body') },
            { key: "s2q1", Icon: ShieldIcon, title: t('rbtInterviewQuestions.s2q1Title'), body: t('rbtInterviewQuestions.s2q1Body') },
            { key: "s2q2", Icon: CalendarIcon, title: t('rbtInterviewQuestions.s2q2Title'), body: t('rbtInterviewQuestions.s2q2Body') },
            { key: "s2q3", Icon: ClipboardIcon, title: t('rbtInterviewQuestions.s2q3Title'), body: t('rbtInterviewQuestions.s2q3Body') },
            { key: "s2q4", Icon: DollarIcon, title: t('rbtInterviewQuestions.s2q4Title'), body: t('rbtInterviewQuestions.s2q4Body') },
            { key: "s2q5", Icon: MessageIcon, title: t('rbtInterviewQuestions.s2q5Title'), body: t('rbtInterviewQuestions.s2q5Body') },
          ]} />
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginTop: 20 }}>
            {t('rbtInterviewQuestions.section2OutroPre')}{" "}
            <SeoLink to="aba-provider-waitlist" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtInterviewQuestions.section2OutroLink')}</SeoLink>{" "}
            {t('rbtInterviewQuestions.section2OutroPost')}
          </p>
        </div>
      </section>

      {/* ── 3. Questions about schedule and setting ───────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t('rbtInterviewQuestions.section3Title')}
          </h2>
          <QGrid items={[
            { key: "s3q1", Icon: CalendarIcon, title: t('rbtInterviewQuestions.s3q1Title'), body: t('rbtInterviewQuestions.s3q1Body') },
            { key: "s3q2", Icon: MapPinIcon, title: t('rbtInterviewQuestions.s3q2Title'), body: t('rbtInterviewQuestions.s3q2Body') },
            { key: "s3q3", Icon: BriefcaseIcon, title: t('rbtInterviewQuestions.s3q3Title'), body: t('rbtInterviewQuestions.s3q3Body') },
            { key: "s3q4", Icon: CheckCircleIcon, title: t('rbtInterviewQuestions.s3q4Title'), body: t('rbtInterviewQuestions.s3q4Body') },
          ]} />
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginTop: 20 }}>
            {t('rbtInterviewQuestions.section3OutroPre')}{" "}
            <SeoLink to="rbt-jobs-miami-fl" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtInterviewQuestions.section3LinkMiami')}</SeoLink>{" "}
            {t('rbtInterviewQuestions.section3OutroOr')}{" "}
            <SeoLink to="rbt-jobs-charlotte-nc" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtInterviewQuestions.section3LinkCharlotte')}</SeoLink>{t('rbtInterviewQuestions.section3OutroMid')}{" "}
            <SeoLink to="aba-case-finder" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtInterviewQuestions.section3LinkCaseFinder')}</SeoLink>{" "}
            {t('rbtInterviewQuestions.section3OutroPost')}
          </p>
        </div>
      </section>

      {/* ── 4. Questions about supervision and support ────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t('rbtInterviewQuestions.section4Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 20 }}>
            {t('rbtInterviewQuestions.section4Intro')}
          </p>
          <QGrid items={[
            { key: "s4q1", Icon: ShieldIcon, title: t('rbtInterviewQuestions.s4q1Title'), body: t('rbtInterviewQuestions.s4q1Body') },
            { key: "s4q2", Icon: CalendarIcon, title: t('rbtInterviewQuestions.s4q2Title'), body: t('rbtInterviewQuestions.s4q2Body') },
            { key: "s4q3", Icon: MessageIcon, title: t('rbtInterviewQuestions.s4q3Title'), body: t('rbtInterviewQuestions.s4q3Body') },
            { key: "s4q4", Icon: CheckCircleIcon, title: t('rbtInterviewQuestions.s4q4Title'), body: t('rbtInterviewQuestions.s4q4Body') },
          ]} />
        </div>
      </section>

      {/* ── 5. Questions about pay, cancellations, and documentation ── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t('rbtInterviewQuestions.section5Title')}
          </h2>
          <QGrid items={[
            { key: "s5q1", Icon: DollarIcon, title: t('rbtInterviewQuestions.s5q1Title'), body: t('rbtInterviewQuestions.s5q1Body') },
            { key: "s5q2", Icon: MessageIcon, title: t('rbtInterviewQuestions.s5q2Title'), body: t('rbtInterviewQuestions.s5q2Body') },
            { key: "s5q3", Icon: ClipboardIcon, title: t('rbtInterviewQuestions.s5q3Title'), body: t('rbtInterviewQuestions.s5q3Body') },
            { key: "s5q4", Icon: CalendarIcon, title: t('rbtInterviewQuestions.s5q4Title'), body: t('rbtInterviewQuestions.s5q4Body') },
          ]} />
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginTop: 20 }}>
            {t('rbtInterviewQuestions.section5OutroPre')}{" "}
            <SeoLink to="aba-timesheet" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtInterviewQuestions.section5LinkTimesheet')}</SeoLink>{" "}
            {t('rbtInterviewQuestions.section5OutroOr')}{" "}
            <SeoLink to="aba-payment-tracker" style={{ color: INDIGO, fontWeight: 700 }}>{t('rbtInterviewQuestions.section5LinkPaymentTracker')}</SeoLink>{" "}
            {t('rbtInterviewQuestions.section5OutroPost')}
          </p>
        </div>
      </section>

      {/* ── 6. How to prepare without overstating experience ─── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('rbtInterviewQuestions.section6Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 20 }}>
            {t('rbtInterviewQuestions.section6Intro')}
          </p>
          <QGrid items={[
            { key: "s6q1", Icon: CheckCircleIcon, title: t('rbtInterviewQuestions.s6q1Title'), body: t('rbtInterviewQuestions.s6q1Body') },
            { key: "s6q2", Icon: CheckCircleIcon, title: t('rbtInterviewQuestions.s6q2Title'), body: t('rbtInterviewQuestions.s6q2Body') },
            { key: "s6q3", Icon: CheckCircleIcon, title: t('rbtInterviewQuestions.s6q3Title'), body: t('rbtInterviewQuestions.s6q3Body') },
            { key: "s6q4", Icon: CheckCircleIcon, title: t('rbtInterviewQuestions.s6q4Title'), body: t('rbtInterviewQuestions.s6q4Body') },
          ]} />
        </div>
      </section>

      <GuideYourPath current="rbt-interview-questions" onNavigate={onNavigate} isMobile={isMobile} />
      <GuideReviewStamp slug="rbt-interview-questions" />
      <PublicFooter />
    </div>
  );
}
